package uz.mint.frontend

import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import org.jetbrains.compose.web.css.*

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    // Anchor links drive the whole nav, so smooth scrolling matters here — but never override
    // a reduced-motion preference.
    ctx.stylesheet.registerStyle("html") {
        cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("no-preference"))) {
            Modifier.scrollBehavior(ScrollBehavior.Smooth)
        }
    }

    // "Twemoji Country Flags" comes first and is not a typeface choice: it is declared in the
    // `<head>` (see `build.gradle.kts`) over `unicode-range: U+1F1E6-1F1FF` alone, so it can only
    // ever be consulted for the regional-indicator pairs the flags are built from. Windows has no
    // flag glyphs in any of its own fonts, and without this every flag on the site renders there
    // as the letter pair — "TR" instead of the Turkish flag. Listing it here rather than on each
    // flag element is what makes it cover the ones added later too.
    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily("Twemoji Country Flags", "Manrope", "system-ui", "-apple-system", "Segoe UI", "Roboto", "sans-serif")
            .fontSize(16.px)
            .lineHeight(1.5)
    }

    // The design draws everything with hard 2px rules and no rounded corners; headings sit
    // tight, so the browser default margins have to go.
    ctx.stylesheet.registerStyleBase("h1, h2, h3, h4, p") { Modifier.margin(0.px) }
    ctx.stylesheet.registerStyleBase("::selection") {
        Modifier.backgroundColor(SitePalettes.light.mint).color(SitePalettes.light.ink)
    }
}

/**
 * The side gutter, in one place.
 *
 * 16px on a phone and 28px from tablet width up. The flat 28px it replaces was written out
 * separately in seven files, which is how they drifted apart, and on a 360px handset it spent 15%
 * of the screen on empty margins — the width a price or a plan name needs to stay on one line.
 *
 * `clamp` rather than a media query so it moves with the viewport instead of jumping at a
 * breakpoint: 4vw lands on 16px at around 400px wide and reaches 28px at 700px.
 */
val PageGutter = clamp(16.px, 4.vw, 28.px)

/**
 * Inner container every full-bleed section shares.
 *
 * **A caller that adds its own vertical padding must repeat [PageGutter] horizontally.** Kobweb
 * emits `.padding(topBottom = x)` as the `padding` shorthand inline, and an inline shorthand beats
 * a class rule for all four sides — so a section that set only its vertical padding silently
 * dropped the side gutter to zero and its text ran into the screen edge. Nine of the ten sections
 * did exactly that, which is why the site looked as though it had no horizontal padding at all
 * on a phone while the rule was sitting right here.
 */
val ShellStyle = CssStyle.base {
    Modifier.fillMaxWidth().maxWidth(1360.px).marginInlineAuto().padding(leftRight = PageGutter)
}

/**
 * One cell of the 2px hairline grid the whole site is drawn with.
 *
 * The lines used to come from the surface *underneath*: a container filled with [SitePalette.rule]
 * showing through 2px gaps. That draws the grid correctly and paints the wrong thing the moment a
 * row is short — the cells that do not exist are still grid tracks, so the rule colour fills them,
 * and a country grid whose last row holds four of six ends in a slab of near-black green. It is
 * only ever noticed in the light theme, where `rule` is `#0F2620`; in the dark one the same slab is
 * a shade off the page and reads as an empty cell.
 *
 * Letting each cell draw its own outline puts the lines exactly where cells are. An outline is
 * painted outside the border box without affecting layout, and two neighbours' outlines meet inside
 * the 2px gap, so a full grid is pixel-identical to what it replaces.
 *
 * **The container must then be filled with whatever the cells are filled with**, not with `rule` —
 * that colour is what shows through where a row runs out.
 */
fun Modifier.hairlineCell(rule: Color): Modifier = outline(2.px, LineStyle.Solid, rule)

/** Small-caps label used for eyebrows and column headings. */
val EyebrowStyle = CssStyle.base {
    Modifier
        .fontSize(12.px)
        .fontWeight(700)
        .letterSpacing(0.14.em)
        .textTransform(com.varabyte.kobweb.compose.css.TextTransform.Uppercase)
}
